import type { FinancialActiveCarrier } from '@/const/Financial'
import type {
  FinancialCharge,
  FinancialCustomerMargin,
  FinancialImport,
  FinancialMarginTable,
  FinancialPriceSuggestion,
} from '@/types/Models/Financial'
import {
  FINANCIAL_ACTIVE_CARRIERS,
  FINANCIAL_BOOKED_CURRENCY,
  FINANCIAL_CARRIER_HANDLES,
  FINANCIAL_MARGIN_REQUESTS,
} from '@/const/Financial'
import {
  financialAmountsEqual,
  multiplyFinancialAmount,
  roundFinancialAmount,
  sumFinancialAmounts,
} from '@/services/FinancialAmounts'
import { financialChargeAmount, financialRole } from '@/services/FinancialImport'
import FinancialProxy from '@/proxies/FinancialProxy'

// Customer prices come from PacSort, never from the carrier invoice. When a shipment is booked, PacSort stores its
// customer price (actualPrice): from the customer's price list, or from a carrier quote plus the customer's margin.
// The delivery is billed at that booked price. Fees, credits and deliveries without a booked price are billed as
// carrier cost plus the customer's margin for that carrier, a markup on cost as in PacSort: 20% = cost × 1.20.

export const financialMarginFactor = (percent: number): string => {
  const total = 100 + Math.round(percent)
  return `${Math.trunc(total / 100)}.${String(total % 100).padStart(2, '0')}`
}

// As PacSort's AddMargin: cost + the margin rounded to öre; the billed amount is then rounded to öre.
export const financialMarkup = (cost: string, percent: number): string => {
  const rounded = Math.round(percent)
  const rate = `${Math.trunc(rounded / 100)}.${String(rounded % 100).padStart(2, '0')}`
  return roundFinancialAmount(sumFinancialAmounts([cost, multiplyFinancialAmount(cost, rate)]), 2)
}

export const financialCustomerMargin = (
  margins: FinancialMarginTable,
  companyId: string,
  carrier: FinancialActiveCarrier,
): FinancialCustomerMargin | null => {
  return margins[companyId]?.[carrier] ?? null
}

const usesBookedPrice = (batch: FinancialImport, row: FinancialCharge): boolean =>
  financialRole(batch, row) === 'base' &&
  row.match?.bookedPrice != null &&
  (!batch.currency || batch.currency === FINANCIAL_BOOKED_CURRENCY)

export const financialPriceSuggestion = (
  batch: FinancialImport,
  row: FinancialCharge,
  margins: FinancialMarginTable,
): FinancialPriceSuggestion | null => {
  if (!row.match) return null
  if (usesBookedPrice(batch, row))
    return {
      amount: roundFinancialAmount(row.match.bookedPrice!, 2),
      source: 'booked',
      marginPercent: null,
      label: 'PacSort booked price',
    }
  const cost = financialChargeAmount(batch, row)
  const margin = financialCustomerMargin(margins, row.match.companyId, batch.carrier)
  if (cost === null || !margin) return null
  return {
    amount: financialMarkup(cost, margin.percent),
    source: 'margin',
    marginPercent: margin.percent,
    label: `Carrier cost + ${margin.percent}% ${margin.custom ? 'customer' : 'default'} margin`,
  }
}

// The price a row is billed at: the reviewer's price from the review grid, otherwise PacSort's price.
export const financialRowPrice = (
  batch: FinancialImport,
  row: FinancialCharge,
  margins: FinancialMarginTable,
): FinancialPriceSuggestion | null => {
  const adjusted = row.adjustment?.customerPrice
  if (adjusted != null)
    return { amount: adjusted, source: 'manual', marginPercent: null, label: `Set in review by ${row.adjustment!.userName}` }
  return financialPriceSuggestion(batch, row, margins)
}

// Excluded, or marked in the review grid not to be billed.
export const financialLeftOut = (row: FinancialCharge): boolean => {
  return row.review?.status === 'excluded' || !!row.adjustment?.exclude
}

// Why a row has no PacSort price, for the reviewer.
export const financialUnpricedReason = (batch: FinancialImport, row: FinancialCharge, margins: FinancialMarginTable): string => {
  if (!row.match) return 'Not linked to a shipment'
  if (financialChargeAmount(batch, row) === null) return 'Invalid invoice amount'
  return margins[row.match.companyId] ? 'No margin for this carrier' : 'Customer margin not loaded'
}

// PacSort's price lists already add an oversize fee to the booked price when the declared size exceeds these
// limits (core domain.Dimensions.Fits, millimetres, smallest to largest side). A CityMail ÖMÅTT row on such a
// shipment may then be billed twice.
const OVERSIZE_LIMITS: Partial<Record<FinancialActiveCarrier, [number, number, number]>> = {
  CityMail: [70, 240, 340],
  PostNord: [70, 240, 340],
  Earlybird: [80, 250, 350],
}

export const financialOversizeInBooking = (batch: FinancialImport, row: FinancialCharge): boolean => {
  const size = row.match?.dimensionsCm
  const limits = OVERSIZE_LIMITS[batch.carrier]
  if (financialRole(batch, row) !== 'additional' || !/ÖMÅTT/i.test(row.description.normalize('NFC')) || !size || !limits) return false
  const sides = [size.length, size.width, size.height].map((value) => Math.round(Number(value ?? 0) * 10))
  if (sides.some((value) => !value)) return false
  const [small, medium, big] = limits
  sides.sort((a, b) => a - b)
  return sides[0] > small || sides[1] > medium || sides[2] > big
}

// Matches saved before sizes were read from PacSort cannot tell whether the oversize fee is already booked.
export const financialSizeUnknown = (batch: FinancialImport, row: FinancialCharge): boolean => {
  return (
    financialRole(batch, row) === 'additional' &&
    /ÖMÅTT/i.test(row.description.normalize('NFC')) &&
    !!row.match &&
    row.match.dimensionsCm === undefined
  )
}

// The delivery cost more than PacSort booked for the customer: 21grams loses money unless the price is corrected.
export const financialLoss = (batch: FinancialImport, row: FinancialCharge): boolean => {
  const cost = financialChargeAmount(batch, row)
  const booked = row.match?.bookedPrice ?? null
  return (
    usesBookedPrice(batch, row) &&
    cost !== null &&
    booked !== null &&
    Number(cost) > Number(booked) &&
    !financialAmountsEqual(cost, booked, 2)
  )
}

const handleCarrier = Object.fromEntries(
  Object.entries(FINANCIAL_CARRIER_HANDLES).map(([carrier, handle]) => [handle, carrier]),
) as Record<string, FinancialActiveCarrier>

// Loads the cost margins of companies not in the table yet. A failed company stays unloaded so its fees are not priced.
export const loadFinancialMargins = async (
  companyIds: string[],
  known: FinancialMarginTable,
  signal: AbortSignal,
): Promise<{ margins: FinancialMarginTable; failed: number }> => {
  const missing = [...new Set(companyIds.filter((id) => id && !known[id]))]
  const proxy = new FinancialProxy()
  const margins: FinancialMarginTable = { ...known }
  let failed = 0
  let next = 0
  const worker = async (): Promise<void> => {
    while (next < missing.length) {
      const companyId = missing[next++]
      try {
        const response = await proxy.findCostMargins(companyId, signal)
        const fallback = response?.meta?.defaultMarginPercent
        if (!Array.isArray(response?.data) || typeof fallback !== 'number') throw new Error('Unexpected cost margins')
        const byCarrier = new Map(response.data.map((margin) => [handleCarrier[margin.carrierHandle], margin]))
        margins[companyId] = Object.fromEntries(
          FINANCIAL_ACTIVE_CARRIERS.map((carrier) => {
            const margin = byCarrier.get(carrier)
            return [
              carrier,
              { percent: margin?.marginPercent ?? fallback, custom: margin?.isCustom ?? false },
            ]
          }),
        )
      } catch (reason) {
        if (signal.aborted) throw reason
        failed += 1
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(FINANCIAL_MARGIN_REQUESTS, missing.length) }, worker))
  return { margins, failed }
}
